import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import style from './style.module.scss';
import {
  IHandlers,
  IProps,
  ITableDividerHeader,
  ITableParams,
  ITableSelectionListItem,
  ManualJoinType,
} from './types';
import {
  getMaterialStyles,
  getReactSelectStyles,
  getTableListWithDividerHeaders,
  autoScrollTableSeater,
  MaxPeopleWarningMsgStart,
  MinPeopleWarningMsgStart,
} from './helpers';
import ReactSelect, {
  components,
  OptionProps,
  MultiValueGenericProps,
  ActionMeta,
  MultiValue,
} from 'react-select';
import { renderIf } from 'external-services/react-utils-service';
import {
  Checkbox,
  Chip,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { JOIN_PREFIX } from 'shared-types/SharedTypes';
import classNames from 'classnames';
import {
  CustomChip,
  CustomMenuItem,
  DividerMenuItem,
  getLabelText,
} from './alt-version-subcomponents';
import AppThemeService from 'internal-services/theme/appTheme.service';
import { isEmpty } from 'lodash-es';
import DropDownHeader from 'design-v2/common/DropDownHeader';
import ModalContainer from 'design-v2/common/ModalContainer';
import PreventDoubleClickToZoom from "design-v2/common/PreventDoubleClickToZoom/index";

const NS = 'TableSeater';

const theme = AppThemeService.getAppTheme();

/**
 * timeout must be outside of component. It is only very short-lived and so shouldn't cause any problems
 * with multiple instances.
 */
let menuClosedTimeout: NodeJS.Timeout = null;

export default function TableSeater({
  sectionsLookup,
  tableSelectionList,
  tableSelectionListWithJoins,
  preselectedTableIds,
  classPrefix,
  isWhiteTheme,
  useReversedColors,
  manualJoinType,
  label,
  handleSelectedChanged,
  isTypeRecurring,
  isDropdownOpen,
  returnToParent,
  showUnderline,
  isDisabled,
  isLockedBooking,
  isMobile,
  isModalStyle = false,
  problemsCallback,
  isWideVersion = false,
  isWideModal = false,
  isDisplayErrMsg = true,
  handleBeforeOpen,
  usePreselectedTableIds = true,
  onChangeByUser = () => {}
}: IProps & IHandlers & ITableParams) {
  const defaultManualJoin = manualJoinType
    ? manualJoinType === ManualJoinType.forceManualJoin
    : false;

  const [manualJoinTables, setManualJoinTables] = useState<boolean>(defaultManualJoin);
  const tableList: ITableSelectionListItem[] = manualJoinTables
    ? tableSelectionList
    : tableSelectionListWithJoins;
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  // const [menuClosedTimeout, setMenuClosedTimeout] = useState<NodeJS.Timeout>(null);
  const [hasSelections, setHasSelections] = useState<boolean>(false);

  const [selectedIdsBeforeOpen, setSelectedIdsBeforeOpen] = useState<string[]>([]);
  const [selectedTablesWithProblems, setSelectedTablesWithProblems] = useState<string[]>([]);

  const [isCancelling, setIsCancelling] = useState(false);

  const selectInputRef = useRef();

  const forceBlur = () => {
    (selectInputRef.current as any).blur();
    if (isModalStyle && isMenuOpen) handleOpenClose(false);
  };

  const forceFocus = () => {
    (selectInputRef.current as any).focus();
  };

  useEffect(() => {
    if (isTypeRecurring && isDropdownOpen) {
      forceFocus();
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      const _selectedIds = getSelectedTables().map((t) => t._id);

      _selectedIds.length > 0 &&
        setTimeout(
          () => {
            autoScrollTableSeater(_selectedIds, tableListWithDividerHeaders);
          },
          isModalStyle ? 15 : 1
        );
    }
  }, [isMenuOpen]);

  const clearSelectedTables = () => {
    (selectInputRef.current as OptionProps<ITableDividerHeader>).clearValue();
  };

  const selectSingleTable = (table: ITableDividerHeader) => {
    (selectInputRef.current as OptionProps<ITableDividerHeader>).setValue([table], 'select-option');
  };

  const selectMultiTable = (tables: ITableDividerHeader[]) => {
    (selectInputRef.current as OptionProps<ITableDividerHeader>).setValue(tables, 'select-option');
  };

  const getReadOnlyValue = (): Readonly<ITableDividerHeader[]> => {
    const val = selectInputRef.current as OptionProps<ITableDividerHeader>;
    return val ? val.getValue() : [];
  };

  const getSelectedTables = (): ITableDividerHeader[] => {
    const readOnlySelectedTables: Readonly<ITableDividerHeader[]> = getReadOnlyValue();
    return tableList.filter((t) => readOnlySelectedTables.some((st) => t._id === st._id));
  };

  useEffect(() => {
    // reset the selected values when a new list is chosen
    clearSelectedTables();
  }, [tableSelectionList]);




  const getSelectedTableProblems = useCallback((_selectedIds: string[]) => {

    const selectedTables: ITableDividerHeader[] = _selectedIds.map(id => tableList.find(t => t._id === id));
    if (!selectedTables?.length) {
      return [];
    }

    const isManualJoin = selectedTables.length > 1;

    const results: string[] = selectedTables.reduce((acc, table) => {
      if (table && table.problemReasons?.length > 0) {
        const newProblems = table.problemReasons
          .filter((problem) => !isManualJoin ? true : problem.indexOf(MaxPeopleWarningMsgStart) !== 0 && problem.indexOf(MinPeopleWarningMsgStart) !== 0)
          .map((problem) => {
            return `Table "${table.name}": ${problem}`;
          });

        acc.push(...newProblems);
      }

      return acc;
    }, []);

    return results;
  }, [tableList]);

  const updateProblems = (_selectedIds: string[]) => {
    const problems = getSelectedTableProblems(_selectedIds);
    if (problemsCallback) {
      problemsCallback(problems);
    } else {
      setSelectedTablesWithProblems(problems || []);
    }
  }

  const tableListWithDividerHeaders: ITableDividerHeader[] = useMemo(() => {
    if (selectInputRef.current) {
      // needs a setTimeout to ensure the React Select UI is updated before we get the selected tables
      setTimeout(() => {
        const _selectedIds = getSelectedTables().map((t) => t._id);
        updateProblems(_selectedIds);
      }, 0);
    }
    return getTableListWithDividerHeaders(tableList);
  }, [tableList]);

  /**
   * This useEffect gets triggered when there is new data coming it externally, like when a new time or service gets
   * selected, which has a different tables list and selected tables in it.
   */
  useEffect(() => {
    if (usePreselectedTableIds) {
      if (!preselectedTableIds?.length || !tableListWithDividerHeaders?.length) {
        if (getReadOnlyValue().length) {
          // if has selection, clear it
          clearSelectedTables();
        }
        setHasSelections(false);
        return;
      }
  
      if (manualJoinTables) {
        const selectedTables = tableListWithDividerHeaders.filter((t) =>
          preselectedTableIds.some((id) => t._id === id)
        );
        setHasSelections(selectedTables.length > 0);
        selectMultiTable(selectedTables);
      } else {
        if (tableListWithDividerHeaders?.length) {
          if (preselectedTableIds?.length > 0) {
            // table join exists - but manual join checkbox is false - onLoad
            const joinTables = tableListWithDividerHeaders.filter(
              (t) => t._id.indexOf(JOIN_PREFIX) === 0
            );
            const joinTableForPreselected = joinTables?.filter((t) =>
              ifJoinTablesEqualToPreSelected(t.tables, preselectedTableIds)
            );
  
            // if joins are found select the join - else select the single table
            if (!isEmpty(joinTableForPreselected)) {
              selectMultiTable(joinTableForPreselected);
              return;
            }
          }
          const selectedTable = tableListWithDividerHeaders.find(
            (t) => t._id === preselectedTableIds[0]
          );
          setHasSelections(!!selectedTable);
          selectSingleTable(selectedTable);
        } else {
          setHasSelections(false);
        }
      }
    }
  // preselectedTableIds.toString() results in failed unit tests
  }, [JSON.stringify(preselectedTableIds), tableListWithDividerHeaders]);

  const ifJoinTablesEqualToPreSelected = (joinTableList: string[], preSelectedTables: string[]) => {
    return joinTableList.every((e) => preSelectedTables.includes(e));
  };

  const useStyles = useMemo(
    () => getMaterialStyles(useReversedColors, isWhiteTheme, classPrefix, true, !!isWideVersion),
    []
  );

  const classes = useStyles();

  const Option = (props: OptionProps<ITableDividerHeader>) => {
    // OptionProps<ITableDividerHeader>

    /**
     * instead of changing the 'value' property of react-select, which causes a full list refresh,
     * instead we're tapping into the individual option on click events and updating individual items
     * in the list
     */
    props.innerProps.onClick = (evt: any) => {
      if (manualJoinTables) {
        props.selectOption(props.data);
      } else {
        // if single select is active, only selects the 1 table
        props.setValue([props.data], 'select-option');
      }
      evt.preventDefault(); // stops it from closing
    };

    return (
      <div className={'auto-scroll-selector'} ref={props.innerRef}>
        <components.Option {...props}>
          <PreventDoubleClickToZoom>
            {props.data.isDivider ? (
              <DividerMenuItem table={props.data} sectionsLookup={sectionsLookup} />
            ) : (
              <CustomMenuItem
                table={props.data}
                manualJoinTables={manualJoinTables}
                isSelected={props.isSelected}
                sectionsLookup={sectionsLookup}
                classes={classes}
              />
            )}
          </PreventDoubleClickToZoom>
        </components.Option>
      </div>
    );
  };

  const MultiValueChip = (props: MultiValueGenericProps<ITableDividerHeader>) => {
    return (
      <div
        style={{
          pointerEvents: 'none',
        }}
      >
        <components.MultiValueContainer {...props}>
          <PreventDoubleClickToZoom>
            <CustomChip
              // key={`chip-item-${props.data._id}-${props.data.isDivider ? 'divider': 'item'}}`}
              table={props.data}
              sectionsLookup={sectionsLookup}
              classes={classes}
            />
          </PreventDoubleClickToZoom>
        </components.MultiValueContainer>
      </div>
    );
  };

  // Multi table seater for recurring booking - its a single line view for multiple tables selected
  const RecurringMultiValueChip = (props: MultiValueGenericProps<ITableDividerHeader>) => {
    const optionSize = (props.selectProps.value as Array<any>).length;
    const isFirstOption = (props.data as any)._id === (props.selectProps.value as any)[0]._id;

    return (
      (optionSize < 2 || isFirstOption) ?
      <div
        style={isWideVersion ? {
          pointerEvents: 'none',
          // flex: 1,
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
        } : {
          pointerEvents: 'none',
        }}
      >
        <div style={isWideVersion ? {
          // display: 'inline-block', 
          flex: 1, 
          // width: '100%',
          maxWidth: optionSize > 1 ? '120px' : '100%',
        } : { display: 'inline-block', width: '190px' }}>
          <components.MultiValueContainer {...props}>
            <PreventDoubleClickToZoom>
              <CustomChip
                // key={`chip-item-${props.data._id}-${props.data.isDivider ? 'divider': 'item'}}`}
                table={props.data}
                sectionsLookup={sectionsLookup}
                classes={classes}
                isTypeRecurring={true}
              />
            </PreventDoubleClickToZoom>
          </components.MultiValueContainer>
        </div>
        {optionSize > 1 && (
          <div style={isWideVersion ? {
            paddingLeft: '8px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '15px',
            fontWeight: 500,
          } : { fontSize: '13px', display: 'inline-block' }}>{` + ${
            optionSize - 1
          }`}</div>
        )}
      </div>
      :
      <></>
    );
  };

  const LabelText = label && getLabelText();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>();

  const handleSelectedIdsBeforeOpen = () => {
    if (manualJoinTables) {
      const _selectedIds = getSelectedTables().map((t) => t._id);
      setSelectedIdsBeforeOpen(_selectedIds);
    }
  };

  const handleOpenClose = async (doOpen: boolean, isCancel: boolean = true) => {
    if (isModalStyle) {
      setIsCancelling(isCancel)
      if (doOpen) {
        if(isWideVersion && !!handleBeforeOpen) {
          await handleBeforeOpen();
        }
        manualJoinTables && handleSelectedIdsBeforeOpen();
        setIsModalOpen(true);
        setTimeout(() => {
          setIsMenuOpen(true);
        }, 1);
      } else {
        /**
         * set as initial value when click cancel
         */
        if (manualJoinTables && isCancel) {
          const selectedTables = tableListWithDividerHeaders.filter((t) =>
            selectedIdsBeforeOpen.some((id) => t._id === id)
          );
          setHasSelections(selectedTables.length > 0);
          selectMultiTable(selectedTables);
          setSelectedIdsBeforeOpen([]);
        }

        setIsModalOpen(false);
      }
    } else {
      setIsMenuOpen(doOpen);
    }
  };

  useEffect(() => {
    isModalStyle &&
      !isModalOpen &&
      setTimeout(() => {
        setIsMenuOpen(false);
      }, 200);
  }, [isModalOpen]);

  const handleSave = () => {
    handleOpenClose(false, false);
    if (manualJoinTables) {
      const _selectedIds = getSelectedTables().map((t) => t._id);
      handleSelectedTables(_selectedIds);

      if (isTypeRecurring) {
        returnToParent();
      }
    }
  };

  const handleSelectedTables = (_selectedIds: string[]) => {
    setHasSelections(_selectedIds.length > 0);
    updateProblems(_selectedIds);
    handleSelectedChanged(_selectedIds, tableList, manualJoinTables);
  };

  return (
    <>
      {isModalStyle && (
        <ModalContainer
          closeTimeoutMS={700}
          isOpen={isModalOpen}
          onRequestClose={() => handleOpenClose(false)}
          isMaxWidth={true}
          isWideVersion={isWideModal}
        >
          <div className={style.tableContainer}>
            <DropDownHeader
              title={'Select Table'}
              isHideSave={!manualJoinTables}
              handleCancel={() => handleOpenClose(false)}
              handleSave={handleSave}
              buttonDisable={false}
            />
            <div className={style.tableList} ref={modalContentRef}></div>
          </div>
        </ModalContainer>
      )}
      <div
        className={classNames({
          [style.root]: true,
          [style.wideRoot]: isWideVersion,
          [style.hasUnderline]: !useReversedColors,
          [style.hasRecurringUnderline]: isTypeRecurring || showUnderline,
        })}
      >
        {LabelText && <LabelText variant="caption">{label}</LabelText>}
        <div className={style.mainRow}>
          {renderIf(isTypeRecurring && !tableListWithDividerHeaders.length, () => (
            <InputLabel
              data-testid="select-label"
              id="table-seater-checkbox-label"
              disableAnimation={true}
              shrink={false}
            >
              No tables available
            </InputLabel>
          ))}

          <div
            className={classNames({
              [style.selectWrap]: true,
              [style.selectRecurringStyle]: isTypeRecurring,
              [style.selectWrapWide]: isWideVersion,
            })}
            data-testid="select-wrap"
          >
            {/*REF: https://react-select.com/components*/}
            <ReactSelect
              ref={selectInputRef}
              options={tableListWithDividerHeaders}
              isMulti // easier to always use multi-select
              isSearchable={false}
              isClearable={false}
              isDisabled={isLockedBooking || (isTypeRecurring && isDisabled)}
              closeMenuOnSelect={!manualJoinTables}
              controlShouldRenderValue={isModalStyle ? !isMenuOpen || !isModalOpen : !isMenuOpen} // no need to render chips while menu is open because it covers them
              menuIsOpen={isModalStyle ? isMenuOpen : isDropdownOpen}
              // menuIsOpen={true} // useful for debugging
              blurInputOnSelect={false} // fixes touch events closing dialog on multi-select
              hideSelectedOptions={false}
              getOptionValue={(option: ITableDividerHeader) => option._id}
              menuPortalTarget={isModalStyle ? modalContentRef.current : document.body}
              placeholder="Select tables"
              components={{
                Option,
                MultiValueContainer: isTypeRecurring ? RecurringMultiValueChip : MultiValueChip,
              }}
              onMenuOpen={() => handleOpenClose(true)}
              onMenuClose={() => {
                if (!manualJoinTables) {
                  forceBlur(); // needs blur to be forced so the chips update and onBlur can be triggered consistently with multi-select
                }
              }}
              // can really use `defaultValue` because table picker changes values when clicking differen times/sections and we want to be fast, so not to reinitialise

              onBlur={() => {
                if (!isModalStyle) {
                  handleOpenClose(false);

                  if (manualJoinTables) {
                    const _selectedIds = getSelectedTables().map((t) => t._id);
                    handleSelectedTables(_selectedIds);

                    if (isTypeRecurring) {
                      returnToParent();
                    }
                    return;
                  } else {
                    if (isTypeRecurring) {
                      returnToParent();
                    }
                  }
                }

                // console.log(NS, 'blur');

                /**
                 * When the menu is closed we can get the value of the selection, but it is not ready straight away,
                 * so here we set a timeout for a second so that the next 'onChange' event has time to trigger.
                 * The 'onChange' handler should clear the timeout easily within this time and execute the 'handleSelectedChanged'.
                 */
                let to: NodeJS.Timeout = null;

                if (!menuClosedTimeout) {
                  to = setTimeout(() => {
                    if (menuClosedTimeout) {
                      clearTimeout(menuClosedTimeout);
                      menuClosedTimeout = null;
                    }
                  }, 400);
                }
                menuClosedTimeout = to;
              }}
              // changing 'value' prop causes the whole list to rerender, which leads to terrible performance
              // value={ensureValueIsArray(selectedTables, manualJoinTables)}

              onChange={(
                newValue: ITableDividerHeader | MultiValue<ITableDividerHeader>,
                actionMeta: ActionMeta<ITableDividerHeader | MultiValue<ITableDividerHeader>>
              ) => {
                if (isCancelling && !(newValue as ITableDividerHeader[]).length) {
                  setIsCancelling(false)
                  return
                }
                // multi-select on needs the onBlur event handler (menuClosedTimeout not needed)
                if (manualJoinTables) {
                  onChangeByUser((newValue as ITableDividerHeader[]).map(item => item._id))
                  return;
                }

                /**
                 * We must not listen for every 'onChange' event, as it should only update the parent container once the
                 * menu is closed. So here we only handle the event when the 'menuClosedTimout' is non-null, which we
                 * set on "blur" (when menu is closed and loses focus).
                 */

                const sendSelectedIdsToParent: boolean = !!menuClosedTimeout;
                const hasValue = Array.isArray(newValue) ? !!newValue[0] : !!newValue

                if (hasValue && sendSelectedIdsToParent) {
                  onChangeByUser((newValue as ITableDividerHeader[]).map(item => item._id))
                  if (menuClosedTimeout) {
                    clearTimeout(menuClosedTimeout);
                  }
                  menuClosedTimeout = null;

                  const _selectedIds = (newValue as ITableDividerHeader[]).map((t) => t._id);
                  handleSelectedTables(_selectedIds);

                  if (isTypeRecurring) {
                    returnToParent();
                  }
                }
              }} // don't use onChange to refresh the value, as it leads to a full list refresh
              styles={getReactSelectStyles(useReversedColors, isTypeRecurring, isMobile, !!isWideVersion)}
            />
          </div>

          {renderIf(!manualJoinType, () => (
            <FormControlLabel
              className={style.checkboxManualJoinFc}
              control={
                <Checkbox
                  data-testid="manual-join-checkbox"
                  className={classes.checkbox}
                  color="default"
                  size="medium"
                  icon={<CheckBoxOutlineBlankIcon fontSize="large" />}
                  checkedIcon={<CheckBoxIcon fontSize="large" />}
                  name="checkedI"
                  checked={manualJoinTables}
                  onChange={(evt, checked) => {
                    const isManualJoin = checked;
                    let selectedTables: ITableDividerHeader[] = getSelectedTables();

                    if (selectedTables?.length && isManualJoin) {
                      // removes joins from selected ids list
                      selectedTables = selectedTables.filter(
                        (t) => t._id.indexOf(JOIN_PREFIX) !== 0
                      );
                    }

                    if (!isManualJoin) {
                      if (selectedTables?.length > 1) {
                        const firstNonJoinTable = selectedTables.find(
                          (t) => t._id.indexOf(JOIN_PREFIX) !== 0
                        );
                        selectedTables = firstNonJoinTable ? [firstNonJoinTable] : [];
                        firstNonJoinTable && selectSingleTable(firstNonJoinTable);
                      } else {
                        clearSelectedTables();
                      }
                    }

                    setManualJoinTables(isManualJoin);

                    const selectedIds = selectedTables.map((t) => t._id);

                    setHasSelections(selectedIds.length > 0);

                    handleSelectedChanged(
                      selectedIds,
                      isManualJoin ? tableSelectionList : tableSelectionListWithJoins,
                      isManualJoin
                    );
                  }}
                />
              }
              label={ 'Manual Join' }
            />
          ))}
        </div>{' '}
        {/* end row2 */}
        {/* Should be implement on TimeSlotMessaging for error msg */}
        {/* {renderIf(showEmptyTableValidationMessage && !hasSelections, () => (
          <Typography
            style={{ color: theme.palette.error.main }}
            className={style.tableValidationMsg}
          >
            No tables selected.
          </Typography>
        ))} */}
      </div>
      {renderIf(selectedTablesWithProblems?.length > 0 && isDisplayErrMsg, () => {
        return selectedTablesWithProblems.map((problem, idx) => (
          <Typography
            key={idx}
            style={{ color: theme.palette.warning.main }}
            className={style.tableValidationMsg}
          >
            {problem}
          </Typography>
        ));
      })}
    </>
  );
}
